import { DatesProvider } from "dates/DatesContext";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { useParams } from "react-router-dom";
import api from "utils/api";
import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import Loading from "components/core/blocks/Loading";
import { useDates } from "dates/DatesContext";
import { useFetch } from "hooks/api";
import { useMemo, useState, Fragment } from "react";
import { Container, Row, Col, Nav, Badge, Table, Card } from "react-bootstrap";
import { buildMetric } from "views/merchandise/metrics";
import Metric from "components/core/blocks/SummaryMetric";
import ReactTable from "react-table-6";
import ReactTablePagination from "components/custom/growth/ProductsCVTablePagination";
import { makeColumn, col } from "components/custom/merchandise/columns";
import useColumnSelector from "hooks/useColumnSelector";
import { Link } from "react-router-dom";
import MultiMetricChart from "components/custom/merchandise/MultiMetricChart";
import useSelectedMetrics from "components/custom/merchandise/hooks/useSelectedMetrics";
import { INVENTORY_STATES } from "components/core/blocks/AdPlan/constants";
import moment from "moment";
import checkApplication from "utils/checkApplication";
import {
  ADVERTISING,
  CONTENT,
  DEALS,
  DYNAMIC_PRICING,
  SALE_STATE_OPTIONS,
} from "components/custom/merchandise/constants";
import ListingQuality from "modules/product_quality/components/ListingQuality";
import { getBadgeVariant } from "modules/product_quality/utils";
import ConversionLegend from "modules/product_quality/components/ConversionLegend";
import ToolTips from "utils/toolTips";
import { useModuleEnabled } from "redux/selectors/modules";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "utils/dashboardLinks";
import useCategories from "hooks/useCategories";
import useProducts from "hooks/useProducts";
import PriceDetails from "views/merchandise/components/PriceDetails";
import { successToast, errorToast } from "utils/toasts";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import { toTitleCase } from "utils/formatText";
import PriceHistorySection from "modules/pricingv2/cards/PriceHistory";
import CompetitorsSection from "modules/pricingv2/cards/Competitors";
import ConfirmationSection from "modules/pricingv2/cards/Confirmation";
import { DemandChartsSection } from "modules/pricingv2/cards/DemandTrend";
import ProductAds from "components/custom/category/advertisements/ProductsAds";
import { formatCurrency, formatNumber, formatPercent } from "utils/formatNumber";
import PricingPlanCard from "modules/pricingv2/PricingPlanCard";
import { cut } from "utils/formatText";
import ExternalProductLink from "components/custom/merchandise/ExternalProductLink";
import SalesMapUSA from "components/custom/maps/usa";
import BuyBoxOffers from "modules/pricingv2/cards/BuyBoxOffers";
import ReferencePrice from "components/custom/ReferencePrice";
import IncomingInventory from "components/custom/IncomingInventory";
import ProductInventory from "components/custom/ProductInventory";
import SalesForecast from "components/custom/SalesForecast";
import { useAppSelector } from "../../redux/hooks";
import ImageGeneration from "components/custom/image-generation/ImageGeneration";
import ProductShopperInsights from "components/custom/merchandise/ProductShopperInsights";
import { ProductSummaryHeader } from "./components/ProductSummaryHeader";
import TrellisNavLink from "components/core/basic/NavLink";
import { PRICING_STATUS_LABELS } from "../../modules/pricingv2/utils";
import CouponReport from "views/merchandise/components/CouponReport";
import { FaCheckCircle } from "react-icons/fa";
import { FaCircleMinus } from "react-icons/fa6";
import { trellisPalette } from "components/custom/analytics/palettes";
import SearchQueryPerformanceProductSection from "modules/sqpr/SearchQueryPerformanceProductSection";

// TODO factor this out into a utils file
const DASHBOARD_TYPES = {
  PRODUCT: "product",
  CATEGORY: "category",
  ROOT: "root",
};

const getDashboardTo = ({ resourceId, type }) => {
  let to;
  switch (type) {
    case DASHBOARD_TYPES.CATEGORY:
      to = `/user/merchandising/category/${resourceId}`;
      break;
    case DASHBOARD_TYPES.PRODUCT:
      to = `/user/merchandising/product/${resourceId}`;
      break;
    default:
      to = `/user/merchandising/dashboard`;
  }
  return to;
};

const ProductDetails = ({ marketPlace, channel, product, start, end, totalUnits, adSpend }) => {
  const { data: pricingOrg } = useFetch(
    ["pricing/settings", marketPlace, channel],
    "/pricing/settings",
    {},
    {
      default: [],
      select: (d) => d.data?.results[0],
    },
  );

  const hasRefPriceModule = useModuleEnabled("REF_PRICE");
  const hasForecastModule = useModuleEnabled("FC");
  const hasInventoryModule = pricingOrg?.use_incoming_inventory;

  const [tab, setTab] = useState("general");

  const getVariations = () => {
    if (!product?.variations) {
      return <span>n/a</span>;
    }

    const keys = Object.keys(product?.variations);
    if (keys.length === 0) {
      return <span>n/a</span>;
    }

    const variations = [];
    for (let i = 0; i < keys.length; i++) {
      const variation = (
        <span className="pe-3" key={keys[i]}>
          {toTitleCase(keys[i])}: {product?.variations[keys[i]]}
        </span>
      );
      variations.push(variation);
    }

    return variations;
  };

  return (
    <Row>
      <Col xs={2} style={{ borderRight: "1px solid #ccc" }}>
        <div className="pt-2"></div>
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setTab(k);
          }}
          activeKey={tab}
          className="flex-column text-start"
        >
          <TrellisNavLink eventKey="general" className="text-start">
            Details
          </TrellisNavLink>

          <TrellisNavLink eventKey="historical-inventory" className="text-start" visible={channel !== "walmart"}>
            Historical Inventory
          </TrellisNavLink>

          <TrellisNavLink
            eventKey="sales-forecast"
            className="text-start"
            visible={channel !== "walmart" && hasForecastModule}
          >
            Sales Forecast
          </TrellisNavLink>

          <TrellisNavLink eventKey="cost-fees" className="text-start" visible={channel === "amazon"}>
            Cost & Fees
          </TrellisNavLink>

          <TrellisNavLink
            eventKey="incoming-inventory"
            className="text-start"
            visible={hasInventoryModule && channel === "amazon"}
          >
            Incoming Inventory
          </TrellisNavLink>

          <TrellisNavLink
            eventKey="ref-price"
            className="text-start"
            visible={hasRefPriceModule && channel === "amazon"}
          >
            Reference Price
          </TrellisNavLink>

          <TrellisNavLink
            eventKey="map"
            className="text-start"
            visible={marketPlace.marketPlace === "USA" && channel === "amazon"}
          >
            Sales By State
          </TrellisNavLink>
        </Nav>
      </Col>
      <Col xs={10}>
        <div className="pt-2">
          {tab === "general" && (
            <Row>
              <Col xs={8}>
                <Table borderless hover>
                  <tbody>
                    <tr>
                      <th>Title</th>
                      <td>{product?.product_title}</td>
                    </tr>
                    <tr>
                      <th>{channel === "amazon" ? `ASIN` : `Channel ID`}</th>
                      <td>
                        {product?.asin} <ExternalProductLink asin={product?.asin} />
                      </td>
                      ``
                    </tr>
                    <tr>
                      <th>SKU</th>
                      <td>{product?.sku}</td>
                    </tr>
                    <tr>
                      <th style={{ whiteSpace: "nowrap" }}>Inventory Qty</th>
                      <td>{product?.product_quantity || product?.product_quantity_fba}</td>
                    </tr>
                    {channel === "amazon" && (
                      <tr>
                        <th>BSR</th>
                        <td>{product?.bsr_rank}</td>
                      </tr>
                    )}
                    <tr>
                      <th>Variations</th>
                      <td>{getVariations()}</td>
                    </tr>
                    {product?.parent_asin && product?.parent_asin !== "n/a" && (
                      <tr>
                        <th>Parent</th>
                        <td>
                          <Link to={`/user/merchandising/parent/${product?.parent_asin}`}>{product?.parent_asin}</Link>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Col>
            </Row>
          )}
          {tab === "historical-inventory" && (
            <Row>
              <Col xs={12}>
                <ProductInventory
                  productIds={[product.id]}
                  start={start}
                  end={end}
                  productPrice={product.product_price}
                  marketPlace={marketPlace.marketPlace}
                  totalUnits={totalUnits}
                />
              </Col>
            </Row>
          )}
          {tab === "sales-forecast" && (
            <Row>
              <Col xs={12}>
                <SalesForecast productIds={[product.id]} marketPlace={marketPlace.marketPlace} adSpend={adSpend} />
              </Col>
            </Row>
          )}
          {tab === "cost-fees" && (
            <Row>
              <Col xs={6}>
                <Table borderless hover>
                  <tbody>
                    <tr>
                      <th>Fulfillment Channel</th>
                      <td>{product?.fulfillment_channel === "AFN" ? "FBA" : "FBM"}</td>
                    </tr>
                    <tr>
                      <th>Landed Cost</th>
                      <td>{formatCurrency(product?.landed_cost, marketPlace)}</td>
                    </tr>
                    {product?.fulfillment_channel !== "AFN" && (
                      <tr>
                        <th>Shipping Cost</th>
                        <td>{formatCurrency(product?.shipping_cost, marketPlace)}</td>
                      </tr>
                    )}
                    {product?.fulfillment_channel === "AFN" && (
                      <tr>
                        <th>Amazon Fulfillment Fees</th>
                        <td>{formatCurrency(product?.fulfillment_fee, marketPlace)}</td>
                      </tr>
                    )}
                    <tr style={{ borderBottom: "1px solid #ccc" }}>
                      <th>Amazon Referral Fees</th>
                      <td>
                        {formatCurrency(product?.channel_fees * product?.product_price, marketPlace)}

                        <span className="ps-3">
                          <small>({formatPercent(product?.channel_fees, 2)})</small>
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <th>Product Price</th>
                      <td>{formatCurrency(product?.product_price, marketPlace)}</td>
                    </tr>
                    <tr
                      style={{
                        borderBottom: "2px solid #ccc",
                      }}
                    >
                      <th>Fees + COGS</th>
                      <td>
                        {formatCurrency(
                          product?.channel_fees * product?.product_price +
                            product?.fulfillment_fee +
                            product?.shipping_cost +
                            product?.landed_cost,
                          marketPlace,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Net Profit</th>
                      <td>
                        {formatCurrency(
                          product?.product_price -
                            (product?.channel_fees * product?.product_price +
                              product?.fulfillment_fee +
                              product?.shipping_cost +
                              product?.landed_cost),
                          marketPlace,
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                <div className="fs-5 pt-2 text-center">
                  <Link to={`/user/pl/report?product_id=${product.id}`} target="_blank">
                    View Detailed Cost & Fees
                  </Link>
                </div>
              </Col>
            </Row>
          )}

          {tab === "ref-price" && <ReferencePrice product={product} marketPlace={marketPlace} />}

          {tab === "map" && (
            <Row>
              <Col xs={12}>
                <SalesMapUSA
                  start_date={start.format("YYYY-MM-DD")}
                  end_date={end.format("YYYY-MM-DD")}
                  marketPlace={marketPlace}
                  productId={product?.id}
                />
              </Col>
            </Row>
          )}

          {tab === "incoming-inventory" && (
            <Row>
              <Col xs={12}>
                <IncomingInventory product={product} />
              </Col>
            </Row>
          )}
        </div>
      </Col>
    </Row>
  );
};

const ContentSection = ({ productId, marketPlace }) => {
  const { loading, data } = useFetch(
    ["productQuality", productId],
    `/merchandise/quality/${productId}`,
    {},
    {
      select: (data) => data?.data,
    },
  );
  const metrics = useMemo(
    () => [
      buildMetric(marketPlace, { total: data }, "seo_score", "seo_score_percent", {
        metric: { accessor: (d) => d?.seo_score },
      }),
      buildMetric(marketPlace, { total: data }, "buyability_score", "buyability_score_percent", {
        metric: { accessor: (d) => d?.buyability_score },
      }),
      buildMetric(marketPlace, { total: data }, "conversion_rate", "conversion_rate_delta", {
        metric: { accessor: (d) => d?.cr },
      }),
      buildMetric(marketPlace, { total: data }, "ctr", "ctr_delta", {
        metric: { accessor: (d) => d?.ctr },
      }),
    ],
    [marketPlace, data],
  );
  const terms = useMemo(() => {
    const seoKeywords = data?.seo_keywords?.keywords;
    const seoSearchTerms = data?.seo_keywords?.search_terms;
    if (seoKeywords && seoSearchTerms) {
      [seoKeywords, seoSearchTerms].forEach((data) => {
        for (let i = 0; i < data.length; i++) {
          const kw = data[i]["keyword_text"];
          const kwData = data[i];
          kwData.cr = 0;
          kwData.ctr = 0;
          kwData.acos = 0;

          if (kw.length <= 1) {
            continue;
          }

          if (kwData.clicks > 0) {
            kwData.cr = kwData.conversions / kwData.clicks;
          }
          if (kwData.impressions > 0) {
            kwData.ctr = kwData.clicks / kwData.impressions;
          }
          if (kwData.sales > 0) {
            kwData.acos = kwData.cost / kwData.sales;
          }

          if (!kwData.cr) {
            kwData.cr = 0;
          }
        }
      });

      seoKeywords.sort(function (a, b) {
        return b.cr - a.cr;
      });

      seoSearchTerms.sort(function (a, b) {
        return b.cr - a.cr;
      });
      return {
        seoKeywords,
        seoSearchTerms,
      };
    }
    return {};
  }, [data]);

  if (loading) {
    return <Loading height={"300px"} fullPage={false} />;
  }
  return (
    <>
      <Row>
        <Col xs={3} className="pt-5 ps-4">
          <Row>
            <Col xs={12}>{data?.content_details && <ListingQuality contentDetails={data?.content_details} />}</Col>
          </Row>
        </Col>
        <Col xs={5} className="pt-5">
          <Row>
            <Col xs={12}>
              <div className="fs-4 pb-2" style={{ fontWeight: "bold" }}>
                Top Converting Keywords
                <ToolTips id={"top-converting-keywords"} toolTip={""}>
                  The top converting keywords for this product.
                  <br />
                  <ConversionLegend xs={6} />
                </ToolTips>
              </div>
              <div
                style={{
                  maxHeight: "600px",
                  overflow: "hidden",
                  overflowY: "scroll",
                  paddingTop: "2px",
                  paddingBottom: "10px",
                }}
              >
                {terms?.seoKeywords &&
                  terms?.seoKeywords?.slice(0, 50)?.map((k) => (
                    <Badge key={k.keyword_text} bg={getBadgeVariant(k)} className="mx-1 my-1 fs-5 badge-keyword">
                      {k.keyword_text}
                    </Badge>
                  ))}
              </div>
            </Col>
          </Row>
        </Col>
        <Col xs={4} className="pt-5">
          <Row>
            {metrics.map((m) => (
              <Col key={m.id} xs={6} style={{ marginBottom: "3rem" }}>
                <Metric
                  toolTip={m.toolTip}
                  name={m.name}
                  value={m.actual}
                  formatOptions={{
                    format: m.format,
                    decimalPlaces: m.decimalPlaces,
                    compact: m.compact,
                  }}
                  compareFormatOptions={m.compareFormatOptions}
                  legendColor={m.color}
                />
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="text-center">
          <Link to={`/user/content/seo?id=${productId}`}>View Product Content</Link>
        </Col>
      </Row>
    </>
  );
};

const CouponPerfIndicator = ({ couponMetric, previousMetric }) => {
  const diffPercent = couponMetric / previousMetric;
  if (diffPercent >= 0.95 && diffPercent <= 1.05) {
    return (
      <span style={{ color: "#aaa" }}>
        <FaCircleMinus />
      </span>
    );
  } else if (diffPercent > 1.05) {
    return (
      <span style={{ color: trellisPalette[12] }}>
        <FaCheckCircle />
      </span>
    );
  }

  return (
    <span style={{ color: "#aaa" }}>
      <FaCircleMinus />
    </span>
  );
};

const DealsSection = ({ productId, marketPlace, categoryId, asin, couponStats, isCouponStatsLoading }) => {
  const [tab, setTab] = useState("coupons");
  const { selectedMetrics, toggleMetric } = useSelectedMetrics(["promo_sales"]);
  const { date, start, end, preStart, preEnd, group } = useDates();

  const showCouponPerformance = false;

  const filters = {
    market_place: marketPlace.marketPlace,
    group_by: group,
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    pre_start: preStart.format("YYYY-MM-DD"),
    pre_end: preEnd.format("YYYY-MM-DD"),
    product_id: productId,
    asin: asin,
  };
  const { data: productDealStats, isLoading: isProductDealStatsLoading } = useFetch(
    ["productDealStats", productId, marketPlace, group, start, end, preStart, preEnd],
    "/merchandise/promos_report/",
    filters,
    {
      select: (data) => data?.data,
    },
  );

  const { data: couponPerformance, isLoading: isCouponPerformanceLoading } = useFetch(
    ["couponPerformance", marketPlace, group, start, end, preStart, preEnd],
    "/merchandise/coupon_performance",
    filters,
    {
      select: (data) => data?.data,
    },
  );

  const metrics = useMemo(
    () => [
      buildMetric(marketPlace, productDealStats, "promo_sales", "promo_sales_percent"),
      buildMetric(marketPlace, productDealStats, "promo_units", "promo_units_percent"),
      buildMetric(marketPlace, productDealStats, "rods", "rods_delta"),
      buildMetric(marketPlace, productDealStats, "promo_cost", "promo_cost_percent"),
    ],
    [marketPlace, productDealStats],
  );

  const columnSpecs = useMemo(() => {
    return [
      col("Promotion", "promotion", "promotion_report_product.promotion_report.promotion_name"),
      col("Status", "text", "status"),
      col("Type", "text", "promotionType"),
      col("Start", "date", "promotion_report_product.promotion_report.start_datetime"),
      col("Deal Price", "currency", "deal_price"),
      col("End", "date", "promotion_report_product.promotion_report.end_datetime"),
      col("Promo Sales", "currency", "product_revenue", {
        compareKey: "sales_est",
        compareType: "est.",
      }),
      col("Promo Units", "number", "product_units_sold", {
        compareKey: "units_est",
        compareType: "est.",
      }),
      col("Total Discounts", "currency", "cost"),
      col("Glance Views", "number", "product_glance_views"),
      col("Conversion Rate", "percent", "conversion"),
    ];
  }, []);

  const promos = useMemo(() => {
    return (
      productDealStats?.promos?.map((p) => {
        const promotionType =
          {
            BEST_DEAL: "Best Deal",
            LIGHTNING_DEAL: "Lightning Deal",
          }?.[p?.promotion_report_product?.promotion_report?.promotion_type] ?? "Unknown";
        let status = p?.promotion_report_product?.promotion_report?.status;
        const start = p?.promotion_report_product?.promotion_report?.start_datetime;
        const end = p?.promotion_report_product?.promotion_report?.end_datetime;
        if (status === "APPROVED") {
          if (moment(start).isBefore(moment()) && moment(end).isAfter(moment())) {
            status = "In Progress";
          } else if (moment(end).isBefore(moment())) {
            status = "Completed";
          } else {
            status = "Approved";
          }
        } else {
          status = status === "CANCELED" ? "Canceled" : status?.toLowerCase;
        }
        return {
          ...p,
          status,
          promotionType,
        };
      }) ?? []
    );
  }, [productDealStats]);

  const createColumn = makeColumn(productDealStats, marketPlace, false, date, false, categoryId);
  const [columns] = useColumnSelector(
    () => columnSpecs.map((c) => createColumn(c.header, c.key, c.type, c.options)),
    [columnSpecs],
  );
  const columnOrder = columns.map((col) => col.id);

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  if (isProductDealStatsLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <Row>
      <Col xs={2} style={{ borderRight: "1px solid #ccc" }}>
        <div className="pt-2"></div>
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setTab(k);
          }}
          activeKey={tab}
          className="flex-column text-start"
        >
          <TrellisNavLink eventKey="coupons" className="text-start">
            Coupons
          </TrellisNavLink>
          <TrellisNavLink eventKey="deals" className="text-start">
            Deals
          </TrellisNavLink>
        </Nav>
      </Col>

      {tab === "deals" && (
        <Col xs={10}>
          <Row>
            <Col xs={8}>
              aaa
              {productDealStats?.dates && (
                <MultiMetricChart
                  metrics={metrics}
                  selectedMetrics={selectedMetrics}
                  data={productDealStats.dates}
                  group={group}
                  handleLegendChange={handleLegendChange}
                />
              )}
            </Col>
            <Col xs={4} className="pt-5">
              <Row>
                {metrics.map((m) => (
                  <Col key={m.id} xs={6} style={{ marginBottom: "3rem" }} onClick={() => toggleMetric(m.id)}>
                    <Metric
                      toolTip={m.toolTip}
                      name={m.name}
                      value={m.actual}
                      target={m.target}
                      formatOptions={{
                        format: m.format,
                        decimalPlaces: m.decimalPlaces,
                        compact: m.compact,
                      }}
                      compareFormatOptions={m.compareFormatOptions}
                      showTarget
                      targetColor={m.target > 0 ? "#007000" : "#d2222d"}
                      legendColor={m.color}
                      targetLabel={"vs. Previous"}
                      selected={selectedMetrics.includes(m.id)}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              {promos?.length > 0 && (
                <ReactTable
                  pageSizeOptions={[5, 10, 20, 25, 50]}
                  showPaginationBottom={false}
                  showPaginationTop
                  PaginationComponent={ReactTablePagination}
                  columns={columns}
                  data={promos ?? []}
                  defaultPageSize={promos?.length ?? 0}
                  className="-highlight"
                  filterable={false}
                  state={columnOrder}
                  getPaginationProps={() => ({
                    hideSearch: true,
                    hideFilters: true,
                    table: "plan-products",
                    style: { marginBottom: "2rem" },
                    carouselLayout: false,
                    disableSearchFocus: true,
                  })}
                />
              )}
            </Col>
          </Row>
        </Col>
      )}
      {tab === "coupons" && (
        <Col xs={10} className="p-4">
          {!isCouponStatsLoading && !isCouponPerformanceLoading && (
            <>
              <Row>
                <Col xs={12}>
                  <div
                    style={{
                      padding: "2rem",
                      border: "1px solid rgb(238, 238, 238)",
                      borderRadius: "6px",
                      backgroundColor: "rgb(248, 248, 248)",
                      marginBottom: "15px",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                    className="fs-5"
                  >
                    <div className="fw-bold">
                      The following table shows the performance of your coupons by discount amount.
                    </div>
                    <div className="ps-2 pt-2 fs-5">
                      <span style={{ color: trellisPalette[12] }}>
                        <FaCheckCircle />
                      </span>{" "}
                      Specifies a positive change in performance compared to a period of time where a coupon was not
                      active.
                    </div>
                    <div className="ps-2 pt-2 fs-5">
                      <span style={{ color: "#aaa" }}>
                        <FaCircleMinus />
                      </span>{" "}
                      Specifies a neutral performance compared to a period of time where a coupon was not active.
                    </div>
                  </div>
                  <Table>
                    <thead>
                      <tr>
                        <th>Discount %</th>
                        <th className="text-start">Sales</th>
                        <th className="text-start">Units</th>
                        <th className="text-start">GCD</th>
                        <th className="text-start">CTR</th>
                        <th className="text-start">CPC</th>
                        <th className="text-start">CR</th>
                        <th className="text-start">BSR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {couponPerformance?.map((c) => (
                        <tr key={c.discount_amount}>
                          <td className="text-center">
                            {c.discount_amount === null ?
                              "All Discounts"
                            : c.discount_type === "PERCENT_OFF_LIST_PRICE" ?
                              `${formatPercent(c.discount_amount / 100, 0)}`
                            : `${formatCurrency(c.discount_amount, marketPlace.marketPlace)}`}
                          </td>
                          <td className="text-center">
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator
                                  couponMetric={c.coupon_total_sales}
                                  previousMetric={c.previous_total_sales}
                                />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>{formatCurrency(c.coupon_total_sales, marketPlace.marketPlace)}</div>
                                  <div>{formatCurrency(c.previous_total_sales, marketPlace.marketPlace)}</div>
                                </Col>
                              )}
                            </Row>
                          </td>

                          <td>
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator
                                  couponMetric={c.coupon_total_units_ordered}
                                  previousMetric={c.previous_total_units_ordered}
                                />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>{formatNumber(c.coupon_total_units_ordered, { decimalPlaces: 0 })}</div>
                                  <div>{formatNumber(c.previous_total_units_ordered, { decimalPlaces: 0 })}</div>
                                </Col>
                              )}
                            </Row>
                          </td>
                          <td className="text-center">
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator couponMetric={c.coupon_gcd} previousMetric={c.previous_gcd} />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>{formatCurrency(c.coupon_gcd, marketPlace.marketPlace)}</div>
                                  <div>{formatCurrency(c.previous_gcd, marketPlace.marketPlace)}</div>
                                </Col>
                              )}
                            </Row>
                          </td>
                          <td className="text-center">
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator couponMetric={c.coupon_ctr} previousMetric={c.previous_ctr} />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>{formatPercent(c.coupon_ctr, 2)}</div>
                                  <div>{formatPercent(c.previous_ctr, 2)}</div>
                                </Col>
                              )}
                            </Row>
                          </td>
                          <td className="text-center">
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator couponMetric={c.coupon_cpc} previousMetric={c.previous_cpc} />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>{formatCurrency(c.coupon_cpc, marketPlace.marketPlace)}</div>
                                  <div>{formatCurrency(c.previous_cpc, marketPlace.marketPlace)}</div>
                                </Col>
                              )}
                            </Row>
                          </td>
                          <td className="text-center">
                            <Row>
                              <Col xs={2}>
                                <CouponPerfIndicator couponMetric={c.coupon_cr} previousMetric={c.previous_cr} />
                              </Col>
                              {showCouponPerformance && (
                                <Col xs={6} className="ps-3">
                                  <div>{formatPercent(c.coupon_cr, 2)}</div>
                                  <div>{formatPercent(c.previous_cr, 2)}</div>
                                </Col>
                              )}
                            </Row>
                          </td>
                          <td className="text-left">
                            <CouponPerfIndicator
                              couponMetric={c.coupon_bsr_rank}
                              previousMetric={c.previous_bsr_rank}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <CouponReport data={couponStats} marketPlace={marketPlace} asinReport />
                </Col>
              </Row>
            </>
          )}
          {(isCouponStatsLoading || isCouponPerformanceLoading) && <Loading height={"300px"} fullPage={false} />}
        </Col>
      )}
    </Row>
  );
};

export const AdvertisingSection = ({ productId, marketPlace }) => {
  return (
    <Row>
      <Col xs={12}>
        <ProductAds productId={productId} marketPlace={marketPlace} />
      </Col>
    </Row>
  );
};

const PricingSection = ({ productId, marketPlace, data, product, isOverviewDataLoading, pricingInfo, user }) => {
  const [tab, setTab] = useState("price_history");

  const { data: pricingProduct, isLoading: isPricingProductDataLoading } = useFetch(
    ["product_pricing", productId],
    `/pricing/products/${productId}/`,
    { get_primary: "false" },
    {
      select: (d) => d?.data,
      placeholderData: keepPreviousData,
    },
  );

  const { data: historyData, isLoading: isHistoryDataLoading } = useFetch(
    ["price_history", productId],
    `/pricing/price_history`,
    {
      product_ids: productId,
    },
    {
      select: (d) => d?.data,
      keepPreviousData: false,
    },
  );

  const showPricingPlanLink = () => {
    return pricingProduct?.plan_id && (user.is_staff || pricingProduct.visible);
  };

  return (
    <Row>
      <Col xs={2} style={{ borderRight: "1px solid #ccc" }}>
        <div className="pt-2"></div>
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setTab(k);
          }}
          activeKey={tab}
          className="flex-column text-start"
        >
          <TrellisNavLink eventKey="price_history" className="text-start">
            Price History
          </TrellisNavLink>
          <TrellisNavLink eventKey="ad_affect" className="text-start">
            Advertising Effectiveness
          </TrellisNavLink>
          <TrellisNavLink eventKey="competitors" className="text-start">
            Competitors
          </TrellisNavLink>

          <TrellisNavLink eventKey="confirmation" className="text-start" vislble={showPricingPlanLink()}>
            Price Changes
          </TrellisNavLink>
          <TrellisNavLink eventKey="pricing_plan" className="text-start" vislble={showPricingPlanLink()}>
            View Pricing Plan
          </TrellisNavLink>
          <TrellisNavLink eventKey="details" className="text-start">
            Amazon Price Details
          </TrellisNavLink>
          <TrellisNavLink eventKey="buy_box" className="text-start">
            Buy Box Offers
          </TrellisNavLink>
        </Nav>
      </Col>
      <Col xs={10} className="p-3">
        <Row className="pb-3 mb-3" style={{ borderBottom: "1px solid #ccc" }}>
          <Col>
            <div className="pe-5 text-center">
              <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                Pricing
              </h6>
              <Badge
                className={`badge bg-trellis-green`}
                pill
                style={{
                  borderRadius: "5px",
                  marginLeft: "1rem",
                }}
              >
                {PRICING_STATUS_LABELS[pricingInfo.status]}
              </Badge>
            </div>
          </Col>
          <Col>
            <div className="pe-5 text-center">
              <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                Enroll Date
              </h6>
              <Badge
                className={`badge bg-trellis-lightgrey text-end`}
                style={{
                  borderRadius: "5px",
                }}
              >
                <span>{pricingInfo.enrollDate ? moment(pricingInfo.enrollDate).format("MM/DD/YYYY") : "N/A"}</span>
              </Badge>
            </div>
          </Col>

          <Col>
            <div className="pe-5 text-center">
              <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                Fixed Price
              </h6>
              <Badge
                className="badge bg-trellis-purple"
                pill
                style={{
                  borderRadius: "5px",
                  marginLeft: "1rem",
                }}
              >
                {pricingInfo.priceOverrideEnd ?
                  formatCurrency(pricingInfo.priceOverride, marketPlace.marketPlace)
                : "No"}
              </Badge>
            </div>
          </Col>

          <Col>
            <div className="pe-5 text-center">
              <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3 text-nowrap">
                Pricing Plan
              </h6>

              <Badge
                className={`badge bg-trellis-lightgrey text-end`}
                style={{
                  borderRadius: "5px",
                }}
              >
                {pricingProduct?.plan_id && (
                  <Link
                    to={`/user/_pricing/plan/${pricingProduct?.plan_id}`}
                    style={{ color: "#000", textDecoration: "none", fontWeight: "bold" }}
                  >
                    Yes
                  </Link>
                )}
                {!pricingProduct?.plan_id && <span>Yes</span>}
              </Badge>
            </div>
          </Col>

          {pricingInfo?.bestPriceDate && (
            <Col>
              <div className="pe-5 text-center">
                <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3 text-nowrap">
                  Competitive Price
                </h6>

                <Badge
                  className={`badge bg-trellis-lightgrey text-end`}
                  style={{
                    borderRadius: "5px",
                  }}
                >
                  {(
                    !isHistoryDataLoading &&
                    historyData[productId] !== undefined &&
                    historyData[productId].external !== null
                  ) ?
                    formatCurrency(historyData[productId].external, marketPlace.marketPlace)
                  : `-`}
                </Badge>
              </div>
            </Col>
          )}
        </Row>

        {tab === "buy_box" && (
          <Row>
            <Col xs={12}>
              <BuyBoxOffers marketPlace={marketPlace} asin={product?.asin} />
            </Col>
          </Row>
        )}

        {tab === "details" && (
          <PriceDetails
            productId={productId}
            marketPlace={marketPlace}
            isHistoryDataLoading={isHistoryDataLoading}
            historyData={historyData}
          />
        )}
        {tab === "pricing_plan" && (
          <>
            <Row className="pb-3 text-center">
              <Col>
                <Link to={`/user/_pricing/plan/${pricingProduct?.plan_id}`}>View Pricing Plan</Link>
              </Col>
            </Row>
            <PricingPlanCard
              planId={pricingProduct?.plan_id}
              hasBorder={false}
              initialTab="configuration"
              showTabs={false}
            />
          </>
        )}
        {tab === "demand_charts" && (
          <DemandChartsSection
            pricingProduct={pricingProduct}
            marketPlace={marketPlace}
            product={product}
            isPricingProductDataLoading={isPricingProductDataLoading}
            data={data}
          />
        )}
        {tab === "price_history" && (
          <>
            <Card>
              <DemandChartsSection
                pricingProduct={pricingProduct}
                marketPlace={marketPlace}
                product={product}
                isPricingProductDataLoading={isPricingProductDataLoading}
                data={data}
              />
            </Card>
            <hr />
            <PriceHistorySection
              pricingProduct={pricingProduct}
              marketPlace={marketPlace}
              isPricingProductDataLoading={isPricingProductDataLoading}
              isLoading={false}
            />
          </>
        )}
        {tab === "ad_affect" && (
          <PriceHistorySection
            pricingProduct={pricingProduct}
            marketPlace={marketPlace}
            isPricingProductDataLoading={isPricingProductDataLoading}
            isLoading={isOverviewDataLoading}
            advertising={true}
          />
        )}
        {tab === "confirmation" && <ConfirmationSection pricingProduct={pricingProduct} marketPlace={marketPlace} />}
        {tab === "competitors" && (
          <CompetitorsSection
            pricingProduct={pricingProduct}
            productId={productId}
            product={product}
            marketPlace={marketPlace}
            isPricingProductDataLoading={isPricingProductDataLoading}
          />
        )}
      </Col>
    </Row>
  );
};

const ProductCard = ({
  productId,
  marketPlace,
  channel,
  user,
  additionalTabs,
  initialTab,
  data,
  isOverviewDataLoading,
}) => {
  const { start, end, group } = useDates();
  const hasAMCModule = useModuleEnabled("AMC");
  const hasDebugModule = useModuleEnabled("DEBUG");
  const isSeller = useAppSelector((state) => state.user.organization_type === "seller");

  const { data: product, isLoading: isProductLoading } = useFetch(
    [],
    `/api/gvads/productid/${productId}`,
    {},
    {
      select: (res) => res.data,
    },
  );

  const filters = {
    market_place: marketPlace.marketPlace,
    group_by: group,
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    product_id: productId,
    asin: product ? product.asin : null,
  };

  const { data: couponStats, isLoading: isCouponStatsLoading } = useFetch(
    ["productDealStats", marketPlace, group, start, end],
    "/merchandise/coupons_reports/",
    filters,
    {
      select: (data) => data?.data,
      enabled: product !== null && product !== undefined,
    },
  );

  const { cellHighlightDates, cellHighlightKey } = useMemo(() => {
    if (!couponStats || !couponStats?.results) return { cellHighlightKey: null, cellHighlightDates: null };
    const dates = [];

    for (const coupon of couponStats.results) {
      const startTime = moment(coupon.start_time);
      const endTime = moment(coupon.end_time);

      let t = startTime;
      while (t.isSameOrBefore(endTime)) {
        dates.push(t.format("MMM DD YYYY"));
        t = t.add(1, "days");
      }
    }

    return { cellHighlightKey: "Units", cellHighlightDates: dates };
  }, [couponStats]);

  const { pricingInfo } = useMemo(() => {
    const pricingInfo = {
      minPrice: null,
      maxPrice: null,
      enrollDate: null,
      status: null,
      priceOverride: null,
      priceOverrideEnd: null,
      productPrice: null,
      bestPriceDate: null,
      amazonStatus: null,
    };

    if (data) {
      pricingInfo.minPrice = data?.products[0]?.min_price;
      pricingInfo.maxPrice = data?.products[0]?.max_price;
      pricingInfo.enrollDate = data?.products[0]?.enroll_date;
      pricingInfo.status = data?.products[0]?.pricing_plan__status;
      pricingInfo.priceOverride = data?.products[0]?.price_override;
      pricingInfo.priceOverrideEnd = data?.products[0]?.price_override_end;
      pricingInfo.productPrice = data?.products[0]?.product_price;
      pricingInfo.bestPriceDate = data?.products[0]?.best_price_date;
      pricingInfo.amazonStatus = data?.products[0]?.amazon_status;
    }

    return { pricingInfo };
  }, [data]);

  const categoryId = data?.products[0]?.category_id;

  const isLoading = isProductLoading || isOverviewDataLoading;

  const overviewMetrics = useMemo(() => {
    if (channel === "shopify") {
      return [
        buildMetric(marketPlace, data, "sales", "sales_percent"),
        buildMetric(marketPlace, data, "units", "units_percent"),
        buildMetric(marketPlace, data, "margin", "margin_delta"),
      ];
    }

    return [
      buildMetric(marketPlace, data, "sales", "sales_percent"),
      buildMetric(marketPlace, data, "units", "units_percent"),
      // buildMetric(marketPlace, data, "total_order_count", null),
      buildMetric(marketPlace, data, "bsr", "bsr_percent", {
        metric: {
          totalOverrideAccessor: "last_bsr_rank",
        },
      }),
      buildMetric(marketPlace, data, "page_views", "page_views_percent"),
      buildMetric(marketPlace, data, "price", "price_delta"),

      buildMetric(marketPlace, data, "margin", "margin_delta"),
      buildMetric(marketPlace, data, "margin_wo_adspend", "margin_wo_adspend_delta"),

      buildMetric(marketPlace, data, "cost", "cost_percent"),
      buildMetric(marketPlace, data, "ad_sales", "ad_sales_percent"),
      buildMetric(marketPlace, data, "ad_spend", "ad_spend_percent"),
      buildMetric(marketPlace, data, "acos", "acos_delta"),
      buildMetric(marketPlace, data, "roas", "roas_delta"),
      buildMetric(marketPlace, data, "total_acos", "total_acos_delta"),
      buildMetric(marketPlace, data, "troas", "troas_delta"),
      buildMetric(marketPlace, data, "roms", "roms_delta"),

      buildMetric(marketPlace, data, "rating", "rating_percent"),

      buildMetric(marketPlace, data, "boosted_profit", null),
      buildMetric(marketPlace, data, "profit", "profit_percent"),
      buildMetric(marketPlace, data, "profit_wo_adspend", "profit_wo_adspend_percent"),

      buildMetric(marketPlace, data, "profit_per_page_view", "profit_per_page_view_percent"),
      buildMetric(marketPlace, data, "profit_per_page_view_wo_adspend", "profit_per_page_view_wo_adspend_percent"),
    ];
  }, [marketPlace, data, channel]);

  const salesStateArray = SALE_STATE_OPTIONS;
  const inventoryStateArray = [
    INVENTORY_STATES.OVERSTOCK,
    INVENTORY_STATES.LOW_STOCK,
    INVENTORY_STATES.OUT_OF_STOCK,
    INVENTORY_STATES.IN_STOCK,
    INVENTORY_STATES.IN_STOCK_SCARCE,
  ];
  const salesLabel = salesStateArray.find((s) => s.value === product?.sales_state)?.label;
  const inventoryBadgeText = inventoryStateArray.find((s) => s.value === product?.stock_status)?.label;
  const [tab, setTab] = useState(initialTab ?? "details");
  const queryClient = useQueryClient();

  const updateTags = useMutation({
    mutationFn: async (updatedTags) => {
      return await api.patch(`/api/gvads/productid/${productId}`, {
        tags: updatedTags,
      });
    },

    onSuccess: () => {
      successToast({
        message: `Product Tags Successfully Updated`,
      });

      queryClient.invalidateQueries({
        queryKey: ["products"],
      });
    },

    onError: (error) => {
      errorToast({
        message: `Failed to Update Product Tags`,
        error,
      });
    },
  });

  return (
    <>
      {isLoading || isCouponStatsLoading ?
        <Loading height={"300px"} fullPage={false} />
      : <Row>
          <Col xs={12} className="mb-4 fs-standard">
            <MetricsCard
              initialSelectedMetric={["sales", "units", "roms"]}
              titleElement={
                <ProductSummaryHeader
                  product={product}
                  inventoryBadgeText={inventoryBadgeText}
                  salesLabel={salesLabel}
                  pricingInfo={pricingInfo}
                  updateTags={updateTags.mutate}
                />
              }
              metrics={overviewMetrics}
              data={data}
              loading={isOverviewDataLoading}
              group={group}
              showMetricsSelector={channel !== "shopify"}
              id={"productDashboard"}
              showAnnotations={true}
              selectedProduct={productId}
              context={"product"}
              extendMetrics={channel !== "shopify"}
              cellHighlightKey={cellHighlightKey}
              cellHighlightDates={cellHighlightDates}
            >
              <Row>
                <Col xs={12} className="mt-5 ph-4 mb-3 border-bottom">
                  <Nav
                    variant="underline"
                    onSelect={(k) => {
                      return setTab(k);
                    }}
                    activeKey={tab}
                  >
                    {additionalTabs?.map((additionalTab) => (
                      <TrellisNavLink
                        eventKey={additionalTab.title?.toLowerCase()}
                        key={additionalTab.title?.toLowerCase()}
                      >
                        {additionalTab.title}
                      </TrellisNavLink>
                    ))}
                    <TrellisNavLink eventKey="details" visible={checkApplication(user, DYNAMIC_PRICING)}>
                      Product Details
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="pricing" visible={checkApplication(user, DYNAMIC_PRICING)}>
                      Dynamic Pricing
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="advertising" visible={checkApplication(user, ADVERTISING)}>
                      Advertising
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="deals" visible={checkApplication(user, DEALS)}>
                      Promos
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="content" visible={checkApplication(user, CONTENT)}>
                      Content
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="loyalty" visible={hasAMCModule && isSeller}>
                      Shopper Insights
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="image-generation" visible={channel === "amazon"}>
                      Image Generation
                    </TrellisNavLink>
                    {hasDebugModule && (
                      <TrellisNavLink eventKey="sqpr" visible={channel === "amazon"}>
                        Search Query Perf
                      </TrellisNavLink>
                    )}
                  </Nav>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  {additionalTabs?.map((additionalTab) => (
                    <Fragment key={additionalTab.title}>
                      {tab === additionalTab.title?.toLowerCase() && additionalTab.content}
                    </Fragment>
                  ))}
                  {tab === "advertising" && <AdvertisingSection productId={productId} marketPlace={marketPlace} />}
                  {tab === "deals" && (
                    <DealsSection
                      productId={productId}
                      marketPlace={marketPlace}
                      channel={channel}
                      data={data}
                      categoryId={categoryId}
                      asin={product?.asin}
                      couponStats={couponStats}
                      isCouponStatsLoading={isCouponStatsLoading}
                    />
                  )}
                  {tab === "pricing" && (
                    <PricingSection
                      productId={productId}
                      marketPlace={marketPlace}
                      channel={channel}
                      data={data}
                      isLoading={isOverviewDataLoading}
                      product={product}
                      pricingInfo={pricingInfo}
                      user={user}
                    />
                  )}
                  {tab === "content" && (
                    <ContentSection productId={productId} marketPlace={marketPlace} channel={channel} data={data} />
                  )}
                  {tab === "details" && (
                    <ProductDetails
                      user={user}
                      product={product}
                      marketPlace={marketPlace}
                      channel={channel}
                      start={start}
                      end={end}
                      totalUnits={data?.total?.total_units}
                      adSpend={data?.total?.cost}
                    />
                  )}
                  {tab === "loyalty" && <ProductShopperInsights productId={productId} marketPlace={marketPlace} />}
                  {tab === "image-generation" && <ImageGeneration productId={productId} asin={product?.asin} />}
                  {tab === "sqpr" && <SearchQueryPerformanceProductSection asin={product.asin} />}
                </Col>
              </Row>
            </MetricsCard>
          </Col>
        </Row>
      }
    </>
  );
};

// Wrapper which fetches data, generates a breadcrumb trail
// and adds the BreadcrumbDates component
const Product = () => {
  const { product: productId = null } = useParams();
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel);

  const hasAgencyDashboardModule = useModuleEnabled("AGENCY_DASHBOARD");

  // Pulls dates from provided context
  const { start, end, preStart, preEnd, group } = useDates();

  // Data fetching

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    group_by: "product_id",
    product_id: productId,
    channel: channel?.currentChannel ?? "amazon",
  };

  const { data, isLoading: isOverviewDataLoading } = useFetch(
    ["product_report", marketPlace.marketPlace, start, end, preStart, preEnd, productId],
    `/api/data_report/product_report`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: !!productId,
    },
  );

  const categoryId = data?.products[0]?.category_id ?? data?.products[0]?.product_category;

  // Breadcrumbs
  const { currentCategoryDetails: categoryDetails, categories: categoryNavData } = useCategories(
    categoryId,
    "merchandising",
  );

  const { currentProductDetails, products: productNavData } = useProducts("merchandising", categoryId, productId);

  const breadCrumbItems = [
    {
      name: "Merchandising Dashboard",
      href: getDashboardTo({
        resourceId: null,
        type: DASHBOARD_TYPES.ROOT,
      }),

      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      unsorted: true,
      dropdownSubtitle: "Dashboards",
    },
    {
      name: categoryDetails?.leaf_category_name,
      href: categoryDetails.href,
      dropdownLinkOptions: categoryNavData,
      searchable: true,
      searchPlaceholderText: "Search categories...",
      dropdownSubtitle: "Categories",
    },
    {
      name: cut(currentProductDetails?.product_title, 50),
      href: currentProductDetails?.href,
      dropdownLinkOptions: productNavData,
      searchable: true,
      searchPlaceholderText: "Search products...",
      dropdownSubtitle: "Products",
    },
  ];

  if (hasAgencyDashboardModule) {
    breadCrumbItems.unshift(AGENCY_DASHBOARD_BREADCRUMB_SPEC);
  }

  return (
    <Container
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="mb-4 px-3">
          <ProductCard
            productId={productId}
            marketPlace={marketPlace}
            user={user}
            channel={channel?.currentChannel ?? "amazon"}
            data={data}
            isOverviewDataLoading={isOverviewDataLoading}
          />
        </Col>
      </Row>
    </Container>
  );
};

const MerchProduct = () => {
  return (
    <DatesProvider>
      <Product />
    </DatesProvider>
  );
};

export default MerchProduct;
